import Handlebars from 'handlebars'
import dayjs from 'dayjs'

// =====================
// COMPARISON HELPERS
// =====================
Handlebars.registerHelper('eq', function (a, b) {
  return a === b
})

Handlebars.registerHelper('ne', function (a, b) {
  return a !== b
})

Handlebars.registerHelper('lt', function (a, b) {
  return a < b
})

Handlebars.registerHelper('gt', function (a, b) {
  return a > b
})

Handlebars.registerHelper('lte', function (a, b) {
  return a <= b
})

Handlebars.registerHelper('gte', function (a, b) {
  return a >= b
})

// =====================
// LOGICAL HELPERS
// =====================
Handlebars.registerHelper('and', function (...args) {
  return args.slice(0, -1).every(Boolean)
})

Handlebars.registerHelper('or', function (...args) {
  return args.slice(0, -1).some(Boolean)
})

Handlebars.registerHelper('not', function (value) {
  return !value
})

Handlebars.registerHelper('xor', function (a, b) {
  return Boolean(a) !== Boolean(b)
})

// =====================
// STRING HELPERS
// =====================
Handlebars.registerHelper('lowercase', function (str) {
  if (typeof str !== 'string') return str
  return str.toLowerCase()
})

Handlebars.registerHelper('uppercase', function (str) {
  if (typeof str !== 'string') return str
  return str.toUpperCase()
})

Handlebars.registerHelper('capitalize', function (str) {
  if (typeof str !== 'string') return str
  return str.charAt(0).toUpperCase() + str.slice(1)
})

Handlebars.registerHelper('titleCase', function (str) {
  if (typeof str !== 'string') return str
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
})

Handlebars.registerHelper('trim', function (str) {
  if (typeof str !== 'string') return str
  return str.trim()
})

Handlebars.registerHelper('substring', function (str, start, end) {
  if (typeof str !== 'string') return str
  return str.substring(start, end)
})

Handlebars.registerHelper('replace', function (str, pattern, replacement) {
  if (typeof str !== 'string') return str
  return str.replace(new RegExp(pattern, 'g'), replacement)
})

Handlebars.registerHelper('concat', function (...args) {
  const values = args.slice(0, -1)
  return values.join('')
})

Handlebars.registerHelper('contains', function (str, substr) {
  if (typeof str !== 'string') return false
  return str.includes(substr)
})

Handlebars.registerHelper('startsWith', function (str, prefix) {
  if (typeof str !== 'string') return false
  return str.startsWith(prefix)
})

Handlebars.registerHelper('endsWith', function (str, suffix) {
  if (typeof str !== 'string') return false
  return str.endsWith(suffix)
})

// =====================
// MATH HELPERS
// =====================
Handlebars.registerHelper('add', function (a, b) {
  return Number(a) + Number(b)
})

Handlebars.registerHelper('subtract', function (a, b) {
  return Number(a) - Number(b)
})

Handlebars.registerHelper('multiply', function (a, b) {
  return Number(a) * Number(b)
})

Handlebars.registerHelper('divide', function (a, b) {
  if (Number(b) === 0) return 0
  return Number(a) / Number(b)
})

Handlebars.registerHelper('mod', function (a, b) {
  return Number(a) % Number(b)
})

Handlebars.registerHelper('floor', function (num) {
  return Math.floor(Number(num))
})

Handlebars.registerHelper('ceil', function (num) {
  return Math.ceil(Number(num))
})

Handlebars.registerHelper('round', function (num) {
  return Math.round(Number(num))
})

Handlebars.registerHelper('toFixed', function (num, digits) {
  return Number(num).toFixed(digits)
})

// =====================
// ARRAY HELPERS
// =====================
Handlebars.registerHelper('first', function (array) {
  if (!Array.isArray(array) || array.length === 0) return null
  return array[0]
})

Handlebars.registerHelper('last', function (array) {
  if (!Array.isArray(array) || array.length === 0) return null
  return array[array.length - 1]
})

Handlebars.registerHelper('nth', function (array, index) {
  if (!Array.isArray(array) || array.length <= index) return null
  return array[index]
})

Handlebars.registerHelper('length', function (value) {
  if (Array.isArray(value)) return value.length
  if (typeof value === 'string') return value.length
  if (value && typeof value === 'object') return Object.keys(value).length
  return 0
})

Handlebars.registerHelper('join', function (array, separator) {
  if (!Array.isArray(array)) return ''
  return array.join(separator || ',')
})

// =====================
// OBJECT HELPERS
// =====================
Handlebars.registerHelper('keys', function (obj) {
  if (!obj || typeof obj !== 'object') return []
  return Object.keys(obj)
})

Handlebars.registerHelper('values', function (obj) {
  if (!obj || typeof obj !== 'object') return []
  return Object.values(obj)
})

Handlebars.registerHelper('has', function (obj, key) {
  if (!obj || typeof obj !== 'object') return false
  return key in obj
})

Handlebars.registerHelper('get', function (obj, path) {
  if (!obj || typeof obj !== 'object') return undefined

  const keys = path.split('.')
  let result = obj

  for (const key of keys) {
    if (result === null || result === undefined) return undefined
    result = result[key]
  }

  return result
})

// =====================
// CONDITIONAL HELPERS
// =====================
Handlebars.registerHelper('if_eq', function (a, b) {
  return a === b ? true : false
})

Handlebars.registerHelper('if_ne', function (a, b) {
  return a !== b ? true : false
})

Handlebars.registerHelper('if_lt', function (a, b) {
  return a < b ? true : false
})

Handlebars.registerHelper('if_gt', function (a, b) {
  return a > b ? true : false
})

Handlebars.registerHelper('if_lte', function (a, b) {
  return a <= b ? true : false
})

Handlebars.registerHelper('if_gte', function (a, b) {
  return a >= b ? true : false
})

Handlebars.registerHelper('default', function (value, defaultValue) {
  return value != null ? value : defaultValue
})

// =====================
// DATE HELPERS
// =====================
Handlebars.registerHelper('formatDate', function (date, format) {
  if (!date) return ''
  try {
    // If a custom format is provided, use dayjs
    if (typeof format === 'string' && format) {
      return dayjs(date).format(format)
    }
    // Default to browser's locale
    const dt = new Date(date)
    return dt.toLocaleDateString()
  } catch (e) {
    return date
  }
})

Handlebars.registerHelper('formatDateLong', function (date, format) {
  if (!date) return ''
  try {
    // If a custom format is provided, use dayjs
    if (typeof format === 'string' && format) {
      return dayjs(date).format(format)
    }
    // Default to locale string with date and time
    const dt = new Date(date)
    return dt.toLocaleString()
  } catch (e) {
    return date
  }
})

Handlebars.registerHelper('now', function () {
  return new Date().toISOString()
})

Handlebars.registerHelper('year', function (date) {
  const dt = date ? new Date(date) : new Date()
  return dt.getFullYear()
})

Handlebars.registerHelper('month', function (date) {
  const dt = date ? new Date(date) : new Date()
  return dt.getMonth() + 1
})

Handlebars.registerHelper('day', function (date) {
  const dt = date ? new Date(date) : new Date()
  return dt.getDate()
})

Handlebars.registerHelper('addDays', function (date, days) {
  try {
    const dt = new Date(date)
    dt.setDate(dt.getDate() + Number(days))
    return dt.toISOString()
  } catch (e) {
    return date
  }
})

// =====================
// FORMAT HELPERS
// =====================
// All formatters can be combined with CRM fields like:
// {{numberFormat wizard.odt21202aabpmoduleypr1.No_Plan.all_ndt_contr_cnt}}
// {{percentage opportunity.total_value 2}}
// Use the Format Field button in the editor to easily construct these expressions
Handlebars.registerHelper('json', function (obj) {
  return JSON.stringify(obj)
})

Handlebars.registerHelper('numberFormat', function (num, locale) {
  try {
    return Number(num).toLocaleString(locale || undefined)
  } catch (e) {
    return num
  }
})

Handlebars.registerHelper('currency', function (num, currency, locale) {
  try {
    // Parse the number value, handling both numeric and string inputs
    const numericValue = typeof num === 'string' ? parseFloat(num) : Number(num)

    // Check if currency is a valid string
    const currencyCode = typeof currency === 'string' && currency.trim() ? currency : 'USD'

    // Format the number with currency
    const formatted = numericValue.toLocaleString(locale || undefined, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      currencyDisplay: 'symbol',
    })
    // For USD, replace the currency name with just the symbol
    if (currencyCode === 'USD') {
      return formatted.replace(/^-?US\$/, (match) => (match.startsWith('-') ? '-$' : '$'))
    }
    return formatted
  } catch (e) {
    return num
  }
})

Handlebars.registerHelper('percentage', function (num, decimals) {
  try {
    const value = Number(num) * 100
    return `${value.toFixed(decimals || 0)}%`
  } catch (e) {
    return num
  }
})

// =====================
// HTML/ENCODING HELPERS
// =====================
Handlebars.registerHelper('encodeURI', function (str) {
  return encodeURIComponent(str)
})

Handlebars.registerHelper('decodeURI', function (str) {
  return decodeURIComponent(str)
})

// Simple HTML escaping
Handlebars.registerHelper('escapeHTML', function (str) {
  if (typeof str !== 'string') return str
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
})

// =====================
// ADVANCED CRM HELPERS
// =====================
Handlebars.registerHelper('phoneFormat', function (phone) {
  if (!phone || typeof phone !== 'string') return phone

  // Strip non-numeric characters
  const cleaned = phone.replace(/\D/g, '')

  // Format based on length
  if (cleaned.length === 10) {
    return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.substring(1, 4)}) ${cleaned.substring(4, 7)}-${cleaned.substring(7)}`
  }

  return phone
})

Handlebars.registerHelper('emailLink', function (email) {
  if (!email || typeof email !== 'string') return email
  return `<a href="mailto:${email}">${email}</a>`
})

Handlebars.registerHelper('phoneLink', function (phone) {
  if (!phone || typeof phone !== 'string') return phone
  const cleaned = phone.replace(/\D/g, '')
  return `<a href="tel:${cleaned}">${phone}</a>`
})

Handlebars.registerHelper('initials', function (name) {
  if (!name || typeof name !== 'string') return ''
  return name
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')
})

Handlebars.registerHelper('pluralize', function (count, singular, plural) {
  return Number(count) === 1 ? singular : plural || `${singular}s`
})

// Useful for displaying only the first N items in a list
Handlebars.registerHelper('limitArray', function (array, limit) {
  if (!Array.isArray(array)) return []
  return array.slice(0, limit)
})

// Format file size in human-readable format
Handlebars.registerHelper('fileSize', function (bytes) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes === 0) return '0 Byte'
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`
})

// Truncate text with ellipsis
Handlebars.registerHelper('truncate', function (str, length) {
  if (typeof str !== 'string') return str
  if (str.length <= length) return str
  return str.substring(0, length) + '...'
})

// Convert a string to a URL-friendly slug
Handlebars.registerHelper('slug', function (str) {
  if (typeof str !== 'string') return str
  return str
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove non-word chars
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
})

// Extract domain from email
Handlebars.registerHelper('emailDomain', function (email) {
  if (typeof email !== 'string' || !email.includes('@')) return ''
  return email.split('@')[1]
})

// Generate a URL for a map location
Handlebars.registerHelper('mapLink', function (address) {
  if (typeof address !== 'string') return ''
  const encodedAddress = encodeURIComponent(address)
  return `https://maps.google.com/?q=${encodedAddress}`
})

export function renderTemplate(template: string, context: Record<string, any> = {}) {
  try {
    return Handlebars.compile(template)(context)
  } catch (e: any) {
    console.warn(e?.message)
    return template
  }
}

// =====================
// CUSTOM HELPERS REGISTRATION
// =====================

export interface CustomHandlebarsHelper {
  name: string
  code: string
  description?: string
}

/**
 * Register a single custom Handlebars helper from code string
 * @param helper - The helper definition with name and code
 * @returns true if registration succeeded, false otherwise
 */
export function registerCustomHelper(helper: CustomHandlebarsHelper): boolean {
  // Validate helper properties
  if (!helper || typeof helper.name !== 'string' || !helper.name.trim()) {
    console.warn('[handlebars] Invalid helper: missing or empty name')
    return false
  }
  if (typeof helper.code !== 'string' || !helper.code.trim()) {
    console.warn(`[handlebars] Invalid helper "${helper.name}": missing or empty code`)
    return false
  }

  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('return ' + helper.code)()
    if (typeof fn !== 'function') {
      console.warn(`[handlebars] Helper "${helper.name}" code does not evaluate to a function`)
      return false
    }
    Handlebars.registerHelper(helper.name, fn)
    console.info(`[handlebars] Registered custom helper: ${helper.name}`)
    return true
  } catch (e: any) {
    console.warn(`[handlebars] Failed to register helper "${helper.name}":`, e?.message)
    return false
  }
}

/**
 * Register multiple custom Handlebars helpers
 * @param helpers - Array of helper definitions
 * @returns Object with counts of successful and failed registrations
 */
export function registerCustomHelpers(helpers: CustomHandlebarsHelper[]): { registered: number; failed: number } {
  let registered = 0
  let failed = 0

  for (const helper of helpers) {
    if (registerCustomHelper(helper)) {
      registered++
    } else {
      failed++
    }
  }

  return { registered, failed }
}

/**
 * Load and register custom Handlebars helpers from installed apps
 * Looks for the "handlebars-helpers" app and registers helpers from its metadata
 * @param installedApps - Array of installed app files
 * @returns Object with counts of successful and failed registrations
 */
export function loadCustomHelpersFromApps(
  installedApps: Array<{ app_metadata?: { name?: string }; metadata?: Record<string, any> }>,
): { registered: number; failed: number } {
  const helpersApp = installedApps.find((app) => app.app_metadata?.name === 'handlebars-helpers')

  if (!helpersApp) {
    console.info('[handlebars] No handlebars-helpers app installed')
    return { registered: 0, failed: 0 }
  }

  const helpers = helpersApp.metadata?.handlebars_helpers as CustomHandlebarsHelper[] | undefined

  if (!helpers || !Array.isArray(helpers) || helpers.length === 0) {
    console.info('[handlebars] handlebars-helpers app has no helpers configured')
    return { registered: 0, failed: 0 }
  }

  console.info(`[handlebars] Loading ${helpers.length} custom helper(s) from handlebars-helpers app`)
  return registerCustomHelpers(helpers)
}
